import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPlay, FaCheck, FaClock, FaEnvelope, FaUtensils, FaTimes, FaMagic, FaCamera, FaExclamationTriangle } from 'react-icons/fa';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ────────────────────────────────────────────────────────────────
// Convierte cualquier URL de Google Drive / YouTube a URL embed
// ────────────────────────────────────────────────────────────────
const buildEmbedUrl = (video) => {
  if (!video) return null;
  const url = video.video_url || '';
  const src = (video.video_source || '').toLowerCase();
  // Google Drive
  const gdMatch = url.match(/(?:\/d\/|id=|\/file\/d\/)([a-zA-Z0-9_-]{20,})/);
  if (src === 'drive' || gdMatch) {
    const fid = gdMatch ? gdMatch[1] : (video.video_id || '');
    if (fid) return `https://drive.google.com/file/d/${fid}/preview`;
  }
  // YouTube
  const ytMatch = url.match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{11})/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&rel=0`;
  return url;
};

// Extrae el file_id de una URL de Google Drive (utilidad, actualmente no
// consumida por los modales — el iframe de Drive resuelve la reproducción).
const getDriveFileId = (video) => {
  if (!video) return null;
  const url = video.video_url || '';
  if (!url.includes('drive.google.com')) return null;
  const m = url.match(/(?:\/d\/|id=|\/file\/d\/)([a-zA-Z0-9_-]{20,})/);
  return m ? m[1] : (video.video_id || null);
};
// Marcador para que ESLint no marque `getDriveFileId` como no usado. Se
// preserva por si a futuro se necesita para tumbnails o metadata de Drive.
export { getDriveFileId as _driveFileIdUtil };

// ────────────────────────────────────────────────────────────────
// Pasos del onboarding (el path del Paso 2 se recibe por prop
// para respetar el plan: básico → /macro-calculator · premium → /personalized-form)
// ────────────────────────────────────────────────────────────────
const buildSteps = (dietFormPath) => ([
  {
    id: 1,
    icon: FaPlay,
    title: 'Ver tu video de bienvenida',
    text: 'Míralo con calma para entender cómo aprovechar tu plan al máximo.',
    accent: '#ec4899',
  },
  {
    id: 2,
    icon: FaUtensils,
    title: 'Llenar el formulario completo',
    text: 'Ve a "Mi Plan de Alimentación" y responde con toda tu información: peso, medidas, objetivo, días de entreno.',
    accent: '#f59e0b',
    action: { label: 'Ir al formulario', to: dietFormPath || '/macro-calculator' },
  },
  {
    id: 3,
    icon: FaCamera,
    title: 'Sube tus fotos iniciales en "Mi Progreso"',
    text: 'Adjunta tus fotos iniciales ahora y súbelas cada semana para llevar registro visual de tu transformación. En esa sección también encontrarás los comentarios personalizados de tu coach.',
    accent: '#06b6d4',
    action: { label: 'Ir a Mi Progreso', to: '/my-progress' },
  },
  {
    id: 4,
    icon: FaClock,
    title: 'Espera 24 horas',
    text: 'Preparamos tu plan 100% personalizado: recetas, rutina, cardio y guía completa.',
    accent: '#8b5cf6',
  },
  {
    id: 5,
    icon: FaEnvelope,
    title: 'Recibirás un email cuando esté listo',
    text: 'Te avisamos apenas tu plan Fit Evolution esté 100% publicado.',
    accent: '#10b981',
  },
]);

// ═══════════════════════════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════════════════════════
export default function WelcomeOnboarding({ token, dietFormPath }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null); // { seen, form_completed, banner_visible, modal_visible }
  const [video, setVideo] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [marking, setMarking] = useState(false);
  const steps = buildSteps(dietFormPath);

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  // Load status + video on mount
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [{ data: st }, { data: vid }] = await Promise.all([
          axios.get(`${API}/user/welcome/status`, { headers: authHeaders }),
          axios.get(`${API}/user/welcome/video`, { headers: authHeaders }),
        ]);
        if (!alive) return;
        setStatus(st);
        setVideo(vid?.video || null);
        if (st?.modal_visible) setModalOpen(true);
      } catch {
        if (!alive) return;
        setStatus({ seen: true, form_completed: false, banner_visible: true, modal_visible: false });
      }
    };
    load();
    return () => { alive = false; };
  }, []);

  const markSeen = async () => {
    if (marking) return;
    setMarking(true);
    try {
      await axios.post(`${API}/user/welcome/mark-seen`, {}, { headers: authHeaders });
      setStatus((prev) => prev ? { ...prev, seen: true, modal_visible: false } : prev);
    } catch { /* silencioso */ }
    setMarking(false);
  };

  const handleCloseModal = async () => {
    setModalOpen(false);
    await markSeen();
  };

  const handleReplayVideo = () => setModalOpen(true);

  // Nada que mostrar
  if (!status || !status.banner_visible) return null;

  const needsUrgentPhotos = !!status.needs_urgent_photos;
  const formCompleted = !!status.form_completed;
  const photosUploaded = !!status.photos_uploaded;
  const isFitEvolution = !!status.is_fit_evolution;

  return (
    <>
      {/* ═══ BANNER URGENTE (>24h sin fotos, solo Fit Evolution) ═══ */}
      {needsUrgentPhotos && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="urgent-photos-banner"
          className="mb-4 rounded-2xl p-4 sm:p-5 relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #7f1d1d 0%, #b91c1c 50%, #dc2626 100%)',
            border: '2px solid rgba(248, 113, 113, 0.6)',
            boxShadow: '0 12px 40px -8px rgba(220, 38, 38, 0.5)',
          }}
        >
          <div className="flex items-start gap-3 sm:gap-4">
            <div
              className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(255, 255, 255, 0.15)' }}
            >
              <FaExclamationTriangle className="text-white text-xl" />
            </div>
            <div className="flex-1">
              <h3 className="text-base sm:text-lg font-black text-white leading-tight">
                ¡Aún faltan tus fotos iniciales! 📸
              </h3>
              <p className="text-xs sm:text-sm text-white/90 mt-1 leading-snug">
                Han pasado más de 24 horas desde que te uniste y todavía no subes tus fotos. Sin ellas tu plan Fit Evolution no puede ser liberado.
              </p>
              <button
                onClick={() => navigate('/my-progress')}
                data-testid="urgent-photos-cta"
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-sm bg-white text-red-700 hover:bg-red-50 transition-all"
              >
                <FaCamera /> Subir mis fotos ahora
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* ═══ BANNER PERSISTENTE ═══ */}
      <WelcomeBanner
        steps={steps}
        video={video}
        onReplayVideo={handleReplayVideo}
        onDismissModal={markSeen}
        modalWasShown={status.seen}
        onNavigate={navigate}
        formCompleted={formCompleted}
        photosUploaded={photosUploaded}
        isFitEvolution={isFitEvolution}
      />

      {/* ═══ MODAL AUTO-OPEN ═══ */}
      <AnimatePresence>
        {modalOpen && (
          <WelcomeModal
            video={video}
            onClose={handleCloseModal}
          />
        )}
      </AnimatePresence>
    </>
  );
}

// ────────────────────────────────────────────────────────────────
// Banner persistente con los 5 pasos
// ────────────────────────────────────────────────────────────────
function WelcomeBanner({ steps, video, onReplayVideo, onNavigate, formCompleted, photosUploaded, isFitEvolution }) {
  // Determinar estado de cada paso: 'done' | 'pending' | 'neutral'
  // - Paso 2 (form): done si formCompleted
  // - Paso 3 (fotos): done si photosUploaded (o si NO es Fit Evolution)
  // - Otros pasos: neutral (informativos)
  const getStepStatus = (stepId) => {
    if (stepId === 2) return formCompleted ? 'done' : 'pending';
    if (stepId === 3) {
      if (!isFitEvolution) return 'neutral';
      return photosUploaded ? 'done' : 'pending';
    }
    return 'neutral';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      data-testid="welcome-banner"
      className="relative overflow-hidden rounded-3xl mb-8"
      style={{
        background: 'linear-gradient(135deg, #fdf2f8 0%, #fff7ed 50%, #f5f3ff 100%)',
        border: '1px solid rgba(var(--brand-primary-glow), 0.2)',
        boxShadow: '0 20px 60px -20px rgba(var(--brand-primary-glow), 0.25)',
      }}
    >
      {/* Fondo decorativo */}
      <div
        aria-hidden
        className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-30 blur-3xl"
        style={{ background: 'radial-gradient(circle, #f472b6, transparent 70%)' }}
      />
      <div
        aria-hidden
        className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full opacity-25 blur-3xl"
        style={{ background: 'radial-gradient(circle, #fbbf24, transparent 70%)' }}
      />

      <div className="relative p-6 sm:p-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex items-start gap-3 sm:gap-4">
            <div
              className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #ec4899, #f59e0b)', boxShadow: '0 8px 20px -4px rgba(var(--brand-primary-glow), 0.5)' }}
            >
              <FaMagic className="text-white text-xl sm:text-2xl" />
            </div>
            <div>
              <div className="text-xs font-black tracking-widest uppercase mb-1" style={{ color: '#be185d' }}>
                Bienvenida a Fit Evolution
              </div>
              <h2 className="text-2xl sm:text-3xl font-black leading-tight" style={{ color: '#831843' }}>
                Estos son tus 5 pasos para arrancar 🎉
              </h2>
              <p className="text-sm text-slate-600 mt-1.5 max-w-xl">
                Sigue los pasos en orden y prepárate para tu plan 100% personalizado. ¡Estamos emocionadas de acompañarte en esta transformación!
              </p>
            </div>
          </div>
        </div>

        {/* 5 pasos en cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {steps.map((step) => {
            const Icon = step.icon;
            const stepStatus = getStepStatus(step.id);
            const isDone = stepStatus === 'done';
            const isPending = stepStatus === 'pending';

            // Estilos según estado
            const cardStyle = isPending
              ? {
                  boxShadow: '0 6px 22px -4px rgba(220, 38, 38, 0.35)',
                  border: '2px solid #ef4444',
                  background: 'linear-gradient(135deg, rgba(254, 226, 226, 0.85), rgba(254, 242, 242, 0.85))',
                }
              : isDone
              ? {
                  boxShadow: '0 4px 16px -6px rgba(16, 185, 129, 0.35)',
                  border: '2px solid #10b981',
                  background: 'linear-gradient(135deg, rgba(209, 250, 229, 0.85), rgba(236, 253, 245, 0.85))',
                }
              : {
                  boxShadow: '0 4px 20px -8px rgba(0,0,0,0.08)',
                };

            return (
              <motion.div
                key={step.id}
                data-testid={`welcome-step-${step.id}`}
                data-step-status={stepStatus}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
                className={`relative rounded-2xl p-4 flex flex-col gap-3 backdrop-blur-sm ${isPending || isDone ? '' : 'bg-white/70 border border-white/60'}`}
                style={cardStyle}
              >
                {/* Badge de estado */}
                {isPending && (
                  <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider" style={{ background: '#ef4444', color: 'white' }}>
                    <FaExclamationTriangle className="text-[9px]" /> Falta
                  </div>
                )}
                {isDone && (
                  <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider" style={{ background: '#10b981', color: 'white' }}>
                    <FaCheck className="text-[9px]" /> Listo
                  </div>
                )}
                {/* Número + icon */}
                <div className="flex items-center gap-2.5">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: `${step.accent}18`, color: step.accent }}
                  >
                    <Icon className="text-lg" />
                  </div>
                  <span className="text-xs font-black tracking-wider uppercase" style={{ color: step.accent }}>
                    Paso {step.id}
                  </span>
                </div>
                <div>
                  <h3 className={`font-bold text-sm leading-snug ${isPending ? 'text-red-800' : isDone ? 'text-emerald-800' : 'text-slate-800'}`}>{step.title}</h3>
                  <p className={`text-xs mt-1 leading-snug ${isPending ? 'text-red-700/80' : isDone ? 'text-emerald-700/80' : 'text-slate-500'}`}>{step.text}</p>
                </div>
                {/* Acción */}
                {step.id === 1 && video && (
                  <button
                    onClick={onReplayVideo}
                    data-testid="welcome-replay-video"
                    className="mt-auto text-xs font-bold px-3 py-1.5 rounded-lg transition-all hover:brightness-110"
                    style={{ background: step.accent, color: 'white' }}
                  >
                    <FaPlay className="inline-block mr-1.5 text-[10px]" /> Ver ahora
                  </button>
                )}
                {step.id === 1 && !video && (
                  <span className="mt-auto text-[10px] italic text-slate-400">
                    Video próximamente
                  </span>
                )}
                {step.action && !isDone && (
                  <button
                    onClick={() => onNavigate(step.action.to)}
                    data-testid={`welcome-step-${step.id}-action`}
                    className="mt-auto text-xs font-bold px-3 py-1.5 rounded-lg transition-all hover:brightness-110"
                    style={{ background: isPending ? '#ef4444' : step.accent, color: 'white' }}
                  >
                    {step.action.label} →
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>

        <p className="text-[11px] text-center text-slate-500 mt-5">
          💡 Este panel desaparecerá automáticamente cuando completes tu formulario y {isFitEvolution ? 'subas tus fotos iniciales' : 'demás pasos'}.
        </p>
      </div>
    </motion.div>
  );
}

// ────────────────────────────────────────────────────────────────
// Modal auto-abierto la primera vez con el video
// ────────────────────────────────────────────────────────────────
function WelcomeModal({ video, onClose }) {
  const embedUrl = buildEmbedUrl(video);
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="welcome-modal"
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(8px)' }}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        transition={{ type: 'spring', damping: 22 }}
        className="relative w-full max-w-3xl rounded-3xl overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #fff7ed 0%, #fdf2f8 100%)',
          boxShadow: '0 40px 100px -20px rgba(0,0,0,0.5)',
        }}
      >
        {/* Botón cerrar */}
        <button
          onClick={onClose}
          data-testid="welcome-modal-close"
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full flex items-center justify-center transition-all hover:brightness-110"
          style={{ background: 'rgba(15,23,42,0.65)', color: 'white' }}
          aria-label="Cerrar"
        >
          <FaTimes />
        </button>

        {/* Header */}
        <div className="px-6 pt-8 pb-4 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3" style={{ background: 'rgba(var(--brand-primary-glow),0.15)', color: '#be185d' }}>
            <FaMagic className="text-xs" />
            <span className="text-[10px] font-black tracking-widest uppercase">Bienvenida</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black leading-tight" style={{ color: '#831843' }}>
            ¡Qué emoción tenerte aquí! 💗
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-xl mx-auto">
            Mira este video para conocer todo lo que Fit Evolution tiene preparado para ti.
          </p>
        </div>

        {/* Video */}
        <div className="px-6 pb-6">
          <div
            className="relative w-full rounded-2xl overflow-hidden"
            style={{ paddingBottom: '56.25%', background: '#111' }}
          >
            {embedUrl ? (
              <iframe
                src={embedUrl}
                title="Video de bienvenida"
                allow="autoplay; encrypted-media; fullscreen"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
                data-testid="welcome-modal-iframe"
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-6 text-center">
                <FaMagic className="text-4xl mb-4 opacity-80" />
                <h3 className="text-xl font-bold mb-2">Video de bienvenida próximamente</h3>
                <p className="text-sm text-slate-300 max-w-md">
                  Mientras tanto, sigue los pasos del panel de abajo para arrancar con tu plan personalizado.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={onClose}
            data-testid="welcome-modal-continue"
            className="w-full sm:w-auto px-6 py-3 rounded-full font-black text-white transition-all hover:brightness-110 flex items-center justify-center gap-2"
            style={{
              background: 'linear-gradient(135deg, #ec4899, #f59e0b)',
              boxShadow: '0 10px 30px -5px rgba(var(--brand-primary-glow),0.5)',
            }}
          >
            <FaCheck /> Entendido, ¡vamos!
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
